import { Link } from '@tanstack/react-router';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@nucleus-commerce/ui/components/badge';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@nucleus-commerce/ui/components/breadcrumb';

import { reference, type DocsOperation } from '../../generated/reference';
import { MethodBadge } from '../method-badge';

/** Breadcrumb, title, method + path with the path parameters marked, and the description. */
export function EndpointHeader({ operation }: { operation: DocsOperation }) {
  const { t } = useTranslation('docs');
  const tag = reference.tags.find((candidate) => candidate.key === operation.tag);

  return (
    <header className="flex flex-col gap-4">
      <Breadcrumb aria-label={t('endpoint.breadcrumb')}>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/docs/api">{t('reference.title')}</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            {tag ? (
              <BreadcrumbLink asChild>
                <Link to="/docs/api" hash={`tag-${tag.key}`}>
                  {tag.title}
                </Link>
              </BreadcrumbLink>
            ) : (
              <BreadcrumbPage>{operation.tag}</BreadcrumbPage>
            )}
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-3xl font-semibold tracking-tight">{operation.summary}</h1>
        {operation.deprecated ? (
          <Badge
            variant="outline"
            className="text-warning-foreground border-warning/40 dark:text-warning"
          >
            {t('endpoint.deprecated')}
          </Badge>
        ) : null}
      </div>
      <div className="bg-muted/40 flex min-w-0 items-center gap-3 rounded-lg border px-3 py-2">
        <MethodBadge method={operation.method} />
        <code className="min-w-0 font-mono text-sm break-all">
          <PathWithParams path={operation.path} />
        </code>
      </div>
      {operation.descriptionHtml ? (
        <div
          className="text-muted-foreground [&_a]:text-foreground [&_code]:bg-muted [&_code]:text-foreground max-w-[72ch] leading-7 [&_a]:underline [&_a]:underline-offset-4 [&_code]:rounded [&_code]:px-1 [&_code]:py-0.5 [&_code]:font-mono [&_code]:text-[0.85em] [&_ol]:list-decimal [&_ol]:pl-6 [&_p+p]:mt-3 [&_ul]:list-disc [&_ul]:pl-6"
          // docs-gen renders the spec's markdown at build time (plan 7.2); the spec is ours.
          dangerouslySetInnerHTML={{ __html: operation.descriptionHtml }}
        />
      ) : null}
    </header>
  );
}

function PathWithParams({ path }: { path: string }) {
  return path.split(/(\{[^}]+\})/).map((part, index) =>
    part.startsWith('{') ? (
      <span key={index} data-path-param={part.slice(1, -1)} className="text-primary font-semibold">
        {part}
      </span>
    ) : (
      <Fragment key={index}>{part}</Fragment>
    ),
  );
}
